import { is } from "bpmn-js/lib/util/ModelUtil";
import BaseRenderer from "diagram-js/lib/draw/BaseRenderer";
import inherits from "inherits-browser";
import { append as svgAppend, create as svgCreate } from "tiny-svg";

// Neutral node colors matched to the designer tokens (--dark-6 stroke, --dark-11 label) so a
// UserTask reads soft-gray like the rest of the diagram; the `.selected` CSS recolors it blue.
// (general.scss also enforces the stroke via `!important`; this keeps the underlying value in sync.)
const NODE_STROKE = "#6e7e96";
const NODE_LABEL = "#17253c";

// Happy Path phase column (a bpmn:Group marked by happyPathPhaseInterceptor). Rendered with a
// HEADER BAND above the column (outside the pool) that acts as its title bar — the interceptor
// puts an interaction hit + the external label there, so select/move/rename all happen on the
// header instead of the hard-to-grab group border. Styled via CSS class (theme tokens).
function isPhaseGroupShape(element) {
  return element.type === "bpmn:Group" && !element.labelTarget && element.businessObject?.isHappyPathPhase === true;
}

const PHASE_HEADER_HEIGHT = 28;

// UserTask icon geometry. The old <image> mapped a 15x18 source viewBox into a 22x22 box at
// (6,6) via the default preserveAspectRatio (xMidYMid meet): height is the limiting dimension,
// so scale = box size / viewBox height, and the shorter (width) axis gets centered in the box.
const USER_ICON_BOX_X = 6;
const USER_ICON_BOX_Y = 6;
const USER_ICON_BOX_SIZE = 22;
const USER_ICON_VIEWBOX_WIDTH = 15;
const USER_ICON_VIEWBOX_HEIGHT = 18;
const USER_ICON_SCALE = USER_ICON_BOX_SIZE / USER_ICON_VIEWBOX_HEIGHT;
const USER_ICON_TRANSLATE_X = USER_ICON_BOX_X + (USER_ICON_BOX_SIZE - USER_ICON_VIEWBOX_WIDTH * USER_ICON_SCALE) / 2;
const USER_ICON_TRANSLATE_Y = USER_ICON_BOX_Y;
// Geometry lifted verbatim from public/images/workflow-icons/user-task-icon.svg (same `d`,
// stroke-width, stroke-miterlimit).
const USER_ICON_HEAD_PATH_D =
  "M7.2704 8.35135C9.30042 8.35135 10.9461 6.7057 10.9461 4.67568C10.9461 2.64566 9.30042 1 7.2704 1C5.24038 1 3.59473 2.64566 3.59473 4.67568C3.59473 6.7057 5.24038 8.35135 7.2704 8.35135Z";
const USER_ICON_BODY_PATH_D =
  "M13.5405 13.1081C13.5405 18.2973 1 18.2973 1 13.1081V12.4504C1 11.8584 1.48 11.3784 2.072 11.3784H12.469C13.061 11.3784 13.541 11.8584 13.541 12.4504V13.1081H13.5405Z";

export default function CustomRenderer(eventBus, bpmnRenderer) {
  BaseRenderer.call(this, eventBus, 1500);
  this.bpmnRenderer = bpmnRenderer;

  this.canRender = function (element) {
    return is(element, "bpmn:UserTask") || isPhaseGroupShape(element);
  };

  this.drawShape = function (parent, shape) {
    if (isPhaseGroupShape(shape)) {
      const root = this.bpmnRenderer.drawShape(parent, shape);
      const headerBand = svgCreate("rect", {
        x: 0,
        y: -PHASE_HEADER_HEIGHT,
        width: shape.width,
        height: PHASE_HEADER_HEIGHT,
        rx: 4,
        ry: 4,
        class: "kuika-hpp-header-band"
      });
      svgAppend(parent, headerBand);
      return root;
    }

    if (!is(shape, "bpmn:UserTask")) {
      return this.bpmnRenderer.drawShape(parent, shape);
    }

    const root = this.bpmnRenderer.drawShape(parent, shape);

    const width = shape.width || 100;
    const overlayHeight = 55;
    const r = 10;

    // White mask over the default renderer's label/border. A plain SVG rect, NOT a
    // foreignObject+div: the mask must survive `saveSVG` → data-URI `<img>` (template
    // thumbnails), where foreignObject HTML content is not reliably painted — the
    // default label bled through and doubled every task name on the exported preview.
    // The class matters: general.scss forces `stroke` onto every `.djs-visual > rect`
    // with !important (to beat bpmn-js's inline strokes), which would paint this mask's
    // square edges as a thin line across the task — the class carries its exemption.
    const labelMask = svgCreate("rect", {
      x: 0,
      y: 0,
      width,
      height: overlayHeight,
      class: "kuika-usertask-mask",
      style: "fill: white; stroke: none;"
    });
    svgAppend(parent, labelMask);

    const overlayBorder = svgCreate("path", {
      d: [
        `M 0 ${overlayHeight}`,
        `V ${r}`,
        `Q 0 0 ${r} 0`,
        `H ${width - r}`,
        `Q ${width} 0 ${width} ${r}`,
        `V ${overlayHeight}`
      ].join(" "),
      style: [
        "fill: none",
        `stroke: ${NODE_STROKE}`,
        "stroke-width: 2px",
        "stroke-linecap: round",
        "stroke-linejoin: round"
      ].join("; ")
    });

    svgAppend(parent, overlayBorder);

    const userIconGroup = svgCreate("g", {
      class: "kuika-usertask-icon",
      transform: `translate(${USER_ICON_TRANSLATE_X.toFixed(2)} ${USER_ICON_TRANSLATE_Y}) scale(${USER_ICON_SCALE.toFixed(4)})`
    });
    userIconGroup.setAttributeNS(null, "data-kuika-usericon", "1");

    // Inline `stroke` stays a presentation ATTRIBUTE (not a `style:` string, unlike the paths
    // above) for two reasons, same convention as NODE_STROKE: template thumbnails are produced
    // by a CSS-less `saveSVG` export, so this is the export's colour; and a presentation
    // attribute has zero specificity, so the live canvas can still be recoloured by
    // `.djs-visual .kuika-usertask-icon path { stroke: token }` in the theme stylesheet — an
    // inline `style` would out-rank that rule and the theme recolor would silently do nothing.
    const userIconHeadPath = svgCreate("path", {
      d: USER_ICON_HEAD_PATH_D,
      stroke: "#818C99",
      "stroke-width": "1.4",
      "stroke-miterlimit": "5",
      fill: "none"
    });
    const userIconBodyPath = svgCreate("path", {
      d: USER_ICON_BODY_PATH_D,
      stroke: "#818C99",
      "stroke-width": "1.4",
      "stroke-miterlimit": "10",
      fill: "none"
    });
    svgAppend(userIconGroup, userIconHeadPath);
    svgAppend(userIconGroup, userIconBodyPath);

    var text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttributeNS(null, "class", "djs-label");
    text.setAttributeNS(
      null,
      "style",
      `font-family: Arial, sans-serif; font-size: 12px; lineHeight: 1.2; font-weight: normal; fill: ${NODE_LABEL};`
    );

    const boxHeight = 80;
    const boxWidth = 20;
    const lines = getLinesOfSize(shape.businessObject.name, 12);
    const startY = (boxHeight + 7.5) / 2 - ((lines.length - 1) * 12) / 2 + 6;

    for (let i = 0; i < lines.length; i++) {
      const tspan = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
      const tspanContent = document.createTextNode(lines[i]);
      tspan.appendChild(tspanContent);
      text.appendChild(tspan);
      tspan.setAttributeNS(null, "x", (boxWidth - tspan.textLength.baseVal.value) / 2 + 0.5);
      tspan.setAttributeNS(null, "y", i * 12 + startY);
    }

    svgAppend(parent, userIconGroup);
    svgAppend(parent, text);

    return root;
  };

  function getLinesOfSize(text, maxLengthPerLine) {
    var lines = [];
    if (typeof text == "undefined" || text == null) {
      return lines;
    }
    var words = text.split(" ");

    for (var i = 0, line = ""; i < words.length; i++) {
      var word = words[i].trim();
      if (word == "") {
        continue;
      }
      if (word.length > maxLengthPerLine) {
        if (line != "") {
          lines.push(line);
          line = "";
        }
        while (word.length > maxLengthPerLine) {
          lines.push(word.slice(0, maxLengthPerLine));
          word = word.slice(maxLengthPerLine);
        }
        line = word;
      } else if (line != "") {
        if (line.concat(word).length + 1 > maxLengthPerLine) {
          lines.push(line);
          line = word;
        } else {
          line = line.concat(" ").concat(word);
        }
      } else {
        line = word;
      }

      if (line.length >= maxLengthPerLine - 1 || (i == words.length - 1 && line != "")) {
        lines.push(line);
        line = "";
      }
    }
    return lines;
  }
}

inherits(CustomRenderer, BaseRenderer);
CustomRenderer.$inject = ["eventBus", "bpmnRenderer"];
